import type { TournamentDetail } from "../../types/swagger";
import { mockMemberNames } from "./members";

const getMemberName = (memberId: string): string =>
  mockMemberNames[memberId] ?? "BYE";

const toBestOfThreeWins = (wins: number): number => (wins === 1 ? 2 : wins);

const STAGE999_EVENT_ID = "fd7909b0-c0fc-44f8-816e-77d20e5a3ddf";
const STAGE888_EVENT_ID = "1f2e3d4c-5b6a-7980-1234-abcdef987654";

const createDetail = (
  eventId: string,
  tournamentId: string,
  memberId: string,
  round: number,
  cellNumber: number,
  blockNumber: number,
  wins: number,
): TournamentDetail => ({
  tournament_id: tournamentId,
  event_id: eventId,
  member_id: memberId,
  round,
  cell_number: cellNumber,
  block_number: blockNumber,
  member_name: getMemberName(memberId),
  wins: toBestOfThreeWins(wins),
});

/** Stage999 用: シングルス トーナメント詳細（16人想定） */
const stage999TournamentDetails: TournamentDetail[] = [
  // round=16: best 16
  createDetail(STAGE999_EVENT_ID, "1", "1", 16, 1, 1, 1),
  createDetail(STAGE999_EVENT_ID, "2", "2", 16, 2, 1, 0),
  createDetail(STAGE999_EVENT_ID, "3", "3", 16, 1, 2, 1),
  createDetail(STAGE999_EVENT_ID, "4", "4", 16, 2, 2, 0),
  createDetail(STAGE999_EVENT_ID, "5", "5", 16, 1, 3, 0),
  createDetail(STAGE999_EVENT_ID, "6", "6", 16, 2, 3, 1),
  createDetail(STAGE999_EVENT_ID, "7", "7", 16, 1, 4, 1),
  createDetail(STAGE999_EVENT_ID, "8", "8", 16, 2, 4, 0),
  createDetail(STAGE999_EVENT_ID, "9", "9", 16, 1, 5, 1),
  createDetail(STAGE999_EVENT_ID, "10", "10", 16, 2, 5, 0),
  createDetail(STAGE999_EVENT_ID, "11", "11", 16, 1, 6, 0),
  createDetail(STAGE999_EVENT_ID, "12", "12", 16, 2, 6, 1),
  createDetail(STAGE999_EVENT_ID, "13", "13", 16, 1, 7, 1),
  createDetail(STAGE999_EVENT_ID, "14", "14", 16, 2, 7, 0),
  createDetail(STAGE999_EVENT_ID, "15", "15", 16, 1, 8, 1),
  createDetail(STAGE999_EVENT_ID, "16", "16", 16, 2, 8, 0),

  // round=8: quarterfinal (winners from round16)
  createDetail(STAGE999_EVENT_ID, "17", "1", 8, 1, 1, 1),
  createDetail(STAGE999_EVENT_ID, "18", "3", 8, 2, 1, 0),
  createDetail(STAGE999_EVENT_ID, "19", "6", 8, 1, 2, 0),
  createDetail(STAGE999_EVENT_ID, "20", "7", 8, 2, 2, 1),
  createDetail(STAGE999_EVENT_ID, "21", "9", 8, 1, 3, 0),
  createDetail(STAGE999_EVENT_ID, "22", "12", 8, 2, 3, 1),
  createDetail(STAGE999_EVENT_ID, "23", "13", 8, 1, 4, 1),
  createDetail(STAGE999_EVENT_ID, "24", "15", 8, 2, 4, 0),

  // round=4: semifinal (winners from round8)
  createDetail(STAGE999_EVENT_ID, "25", "1", 4, 1, 1, 1),
  createDetail(STAGE999_EVENT_ID, "26", "7", 4, 2, 1, 0),
  createDetail(STAGE999_EVENT_ID, "27", "12", 4, 1, 2, 0),
  createDetail(STAGE999_EVENT_ID, "28", "13", 4, 2, 2, 1),

  // round=2: final (winners from semifinal)
  createDetail(STAGE999_EVENT_ID, "29", "1", 2, 1, 1, 0),
  createDetail(STAGE999_EVENT_ID, "30", "13", 2, 2, 1, 1),
];

/** Stage888 用: シングルス トーナメント詳細（15人想定。1枠はBYE） */
const stage888TournamentDetails: TournamentDetail[] = [
  // round=16: best 16 (block 8 is single player -> BYE)
  createDetail(STAGE888_EVENT_ID, "101", "1", 16, 1, 1, 1),
  createDetail(STAGE888_EVENT_ID, "102", "2", 16, 2, 1, 0),
  createDetail(STAGE888_EVENT_ID, "103", "3", 16, 1, 2, 1),
  createDetail(STAGE888_EVENT_ID, "104", "4", 16, 2, 2, 0),
  createDetail(STAGE888_EVENT_ID, "105", "5", 16, 1, 3, 1),
  createDetail(STAGE888_EVENT_ID, "106", "6", 16, 2, 3, 0),
  createDetail(STAGE888_EVENT_ID, "107", "7", 16, 1, 4, 1),
  createDetail(STAGE888_EVENT_ID, "108", "8", 16, 2, 4, 0),
  createDetail(STAGE888_EVENT_ID, "109", "9", 16, 1, 5, 1),
  createDetail(STAGE888_EVENT_ID, "110", "10", 16, 2, 5, 0),
  createDetail(STAGE888_EVENT_ID, "111", "11", 16, 1, 6, 0),
  createDetail(STAGE888_EVENT_ID, "112", "12", 16, 2, 6, 1),
  createDetail(STAGE888_EVENT_ID, "113", "13", 16, 1, 7, 1),
  createDetail(STAGE888_EVENT_ID, "114", "14", 16, 2, 7, 0),
  createDetail(STAGE888_EVENT_ID, "115", "15", 16, 1, 8, 1),

  // round=8: quarterfinal (winners from round16)
  createDetail(STAGE888_EVENT_ID, "116", "1", 8, 1, 1, 1),
  createDetail(STAGE888_EVENT_ID, "117", "3", 8, 2, 1, 0),
  createDetail(STAGE888_EVENT_ID, "118", "5", 8, 1, 2, 1),
  createDetail(STAGE888_EVENT_ID, "119", "7", 8, 2, 2, 0),
  createDetail(STAGE888_EVENT_ID, "120", "9", 8, 1, 3, 0),
  createDetail(STAGE888_EVENT_ID, "121", "12", 8, 2, 3, 1),
  createDetail(STAGE888_EVENT_ID, "122", "13", 8, 1, 4, 1),
  createDetail(STAGE888_EVENT_ID, "123", "15", 8, 2, 4, 0),

  // round=4: semifinal (winners from round8)
  createDetail(STAGE888_EVENT_ID, "124", "1", 4, 1, 1, 0),
  createDetail(STAGE888_EVENT_ID, "125", "5", 4, 2, 1, 1),
  createDetail(STAGE888_EVENT_ID, "126", "12", 4, 1, 2, 1),
  createDetail(STAGE888_EVENT_ID, "127", "13", 4, 2, 2, 0),

  // round=2: final (winners from semifinal)
  createDetail(STAGE888_EVENT_ID, "128", "5", 2, 1, 1, 1),
  createDetail(STAGE888_EVENT_ID, "129", "12", 2, 2, 1, 0),
];

export const mockTournamentDetails: TournamentDetail[] = [
  ...stage999TournamentDetails,
  ...stage888TournamentDetails,
];
